import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// ── shared dark-mode hook ─────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      return localStorage.getItem("tg-theme") === "dark";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setDark((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("tg-theme", next ? "dark" : "light");
      } catch {}
      return next;
    });
  return [dark, toggle];
}

// ── color tokens ─────────────────────────────────────────────────────────────
const tok = (D) => ({
  bg:       D ? "#0f172a" : "#f0fdf4",
  surface:  D ? "#1e293b" : "#ffffff",
  surface2: D ? "#253347" : "#f8fafc",
  border:   D ? "#334155" : "#e2e8f0",
  textPri:  D ? "#f1f5f9" : "#1e293b",
  textSec:  D ? "#94a3b8" : "#64748b",
});

// ── difficulty config (no lucide, inline SVG emoji approach) ─────────────────
const diffConfig = {
  easy:   { emoji: "⚡", label: "Easy",   accentBg: "#d1fae5", accentText: "#065f46" },
  medium: { emoji: "🎯", label: "Medium", accentBg: "#fef3c7", accentText: "#92400e" },
  hard:   { emoji: "🔥", label: "Hard",   accentBg: "#ffe4e6", accentText: "#9f1239" },
  mixed:  { emoji: "🔀", label: "Mixed",  accentBg: "#ede9fe", accentText: "#4c1d95" },
};

// ── small helpers ─────────────────────────────────────────────────────────────
const initials = (name) => (name ? name.charAt(0).toUpperCase() : "?");
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");

// ── main component ────────────────────────────────────────────────────────────
const ChallengePage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const challengeIdParam = searchParams.get("id");

  const [activeTab, setActiveTab] = useState("invite");
  const [opponentEmail, setOpponentEmail] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [sending, setSending] = useState(false);
  const [challenges, setChallenges] = useState({ received: [], sent: [], active: [] });
  const [leaderboard, setLeaderboard] = useState({ monthly: [], allTime: [] });
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  const [D, toggleDark] = useDarkMode();
  const t = tok(D);

  useEffect(() => {
    fetchChallenges();
    fetchLeaderboard();
    fetchHistory();

    // Auto-switch to pending tab if there's a challenge ID
    if (challengeIdParam) {
      setActiveTab("pending");
    }
  }, [token]);

  // Poll for updates when on active tab – also fetch immediately on switch
  useEffect(() => {
    if (activeTab === "active") {
      fetchChallenges();                                   // immediate refresh
      const interval = setInterval(fetchChallenges, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const fetchChallenges = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setChallenges(data);
      }
    } catch (error) {
      console.error("Failed to fetch challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const [monthlyRes, allTimeRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/challenge/leaderboard/monthly`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/challenge/leaderboard/all-time`),
      ]);

      if (monthlyRes.ok && allTimeRes.ok) {
        const monthly = await monthlyRes.json();
        const allTime = await allTimeRes.json();
        setLeaderboard({ monthly: monthly.leaderboard, allTime: allTime.leaderboard });
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const sendChallenge = async () => {
    if (!opponentEmail) {
      toast.error("Please enter opponent's email");
      return;
    }

    setSending(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            opponent_email: opponentEmail,
            difficulty,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setOpponentEmail("");
        fetchChallenges();
        setActiveTab("pending");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send challenge");
      }
    } catch (error) {
      toast.error("Connection error");
    } finally {
      setSending(false);
    }
  };

  const respondToChallenge = async (challengeId, accept) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/${challengeId}/respond`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ challenge_id: challengeId, accept }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchChallenges();
        if (accept) {
          setActiveTab("active");
        }
      } else {
        toast.error("Failed to respond");
      }
    } catch (error) {
      toast.error("Connection error");
    }
  };

  const startChallengeQuiz = (challengeId) => {
    navigate(`/challenge-quiz/${challengeId}`);
  };

  // ── tab definitions ──────────────────────────────────────────────────────
  const tabs = [
    { id: "invite",      label: "Challenge Friend", icon: "⚔️",  badge: 0 },
    { id: "pending",     label: "Pending",          icon: "⏳",  badge: challenges.received?.length || 0 },
    { id: "active",      label: "Active",           icon: "🔥",  badge: challenges.active?.length || 0 },
    { id: "leaderboard", label: "Leaderboard",      icon: "🏆",  badge: 0 },
    { id: "history",     label: "History",          icon: "📋",  badge: 0 },
  ];

  // ── row highlight color for leaderboard ──────────────────────────────────
  const podiumRowStyle = (idx, D, t) => {
    if (idx === 0) return D ? "#2d2000" : "#fffbeb";
    if (idx === 1) return D ? "#1c2533" : "#f8fafc";
    if (idx === 2) return D ? "#2a1a00" : "#fff7ed";
    return t.surface;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: t.bg,
        fontFamily: "'Inter', 'Nunito', sans-serif",
        transition: "background 0.3s",
      }}
    >
      {/* ── keyframes ── */}
      <style>{`
        @keyframes swordSpin {
          0%   { transform: rotate(-15deg) scale(1);   }
          50%  { transform: rotate(15deg)  scale(1.08); }
          100% { transform: rotate(-15deg) scale(1);   }
        }
        @keyframes spinnerDot {
          0%, 80%, 100% { opacity: 0; }
          40%           { opacity: 1; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 12px rgba(16,185,129,0.4); }
          50%       { box-shadow: 0 0 28px rgba(16,185,129,0.8); }
        }
        .tab-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: t.surface,
          borderBottom: `1px solid ${t.border}`,
          boxShadow: D
            ? "0 1px 12px rgba(0,0,0,0.4)"
            : "0 1px 8px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            padding: "0 20px",
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Back */}
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: t.textSec,
              fontSize: 14,
              fontWeight: 600,
              padding: "6px 10px",
              borderRadius: 8,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>

          {/* Logo */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <span style={{ fontSize: 22 }}>⚔️</span>
            <span
              style={{
                fontFamily: "Nunito, sans-serif",
                fontWeight: 800,
                fontSize: 20,
                color: t.textPri,
                letterSpacing: "-0.02em",
              }}
            >
              Tax Genius
            </span>
          </div>

          {/* Dark toggle */}
          <button
            onClick={toggleDark}
            title="Toggle dark mode"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: `1px solid ${t.border}`,
              background: t.surface2,
              cursor: "pointer",
              fontSize: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {D ? "☀️" : "🌙"}
          </button>
        </div>
      </header>

      {/* ── MAIN ──────────────────────────────────────────────────────────── */}
      <main
        style={{ maxWidth: 800, margin: "0 auto", padding: "28px 20px 72px" }}
      >
        {/* ── HORIZONTAL TAB BAR ──────────────────────────────────────────── */}
        <div
          className="tab-scroll"
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            paddingBottom: 4,
            marginBottom: 28,
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "9px 18px",
                  borderRadius: 9999,
                  border: active ? "none" : `1px solid ${t.border}`,
                  background: active
                    ? "linear-gradient(135deg,#10b981,#059669)"
                    : t.surface,
                  color: active ? "#fff" : t.textSec,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  boxShadow: active
                    ? "0 4px 14px rgba(16,185,129,0.3)"
                    : "none",
                  transition: "all 0.2s",
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.badge > 0 && (
                  <span
                    style={{
                      background: "#ef4444",
                      color: "#fff",
                      borderRadius: 9999,
                      fontSize: 11,
                      fontWeight: 800,
                      minWidth: 18,
                      height: 18,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 5px",
                    }}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── TAB CONTENT ─────────────────────────────────────────────────── */}
        <AnimatePresence mode="wait">

          {/* ═══════════════════════ INVITE TAB ═══════════════════════════════ */}
          {activeTab === "invite" && (
            <motion.div
              key="invite"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.28 }}
            >
              {/* Hero banner */}
              <div
                style={{
                  background: D
                    ? "linear-gradient(135deg,#0f2027,#203a43,#2c5364)"
                    : "linear-gradient(135deg,#064e3b,#065f46,#047857)",
                  borderRadius: "24px 24px 0 0",
                  padding: "36px 24px 28px",
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Animated swords */}
                <div
                  style={{
                    fontSize: 56,
                    marginBottom: 12,
                    display: "inline-block",
                    animation: "swordSpin 2.4s ease-in-out infinite",
                  }}
                >
                  ⚔️
                </div>
                <h2
                  style={{
                    fontFamily: "Nunito, sans-serif",
                    fontWeight: 800,
                    fontSize: 26,
                    color: "#fff",
                    margin: "0 0 8px",
                  }}
                >
                  Challenge a Friend
                </h2>
                <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, margin: 0 }}>
                  Compete head-to-head. Highest score + fastest time wins!
                </p>
              </div>

              {/* Form body */}
              <div
                style={{
                  background: t.surface,
                  border: `1px solid ${t.border}`,
                  borderTop: "none",
                  borderRadius: "0 0 24px 24px",
                  padding: 28,
                }}
              >
                {/* Email input */}
                <div style={{ marginBottom: 24 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 700,
                      color: t.textSec,
                      marginBottom: 8,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Opponent's Email
                  </label>
                  <input
                    type="email"
                    placeholder="friend@example.com"
                    value={opponentEmail}
                    onChange={(e) => setOpponentEmail(e.target.value)}
                    data-testid="opponent-email-input"
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      height: 48,
                      borderRadius: 12,
                      border: `1px solid ${t.border}`,
                      background: t.surface2,
                      color: t.textPri,
                      fontSize: 15,
                      padding: "0 16px",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#10b981")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = t.border)
                    }
                  />
                  <p
                    style={{
                      color: t.textSec,
                      fontSize: 12,
                      marginTop: 6,
                    }}
                  >
                    They must have a Tax Genius account to accept
                  </p>
                </div>

                {/* Difficulty grid */}
                <div style={{ marginBottom: 28 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 700,
                      color: t.textSec,
                      marginBottom: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Difficulty
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 10,
                    }}
                  >
                    {Object.entries(diffConfig).map(([key, cfg]) => {
                      const active = difficulty === key;
                      return (
                        <button
                          key={key}
                          onClick={() => setDifficulty(key)}
                          data-testid={`challenge-difficulty-${key}`}
                          style={{
                            padding: "14px 8px",
                            borderRadius: 14,
                            border: active
                              ? "2px solid #10b981"
                              : `2px solid ${t.border}`,
                            background: active
                              ? D
                                ? "rgba(16,185,129,0.12)"
                                : "#f0fdf4"
                              : t.surface2,
                            cursor: "pointer",
                            transition: "all 0.18s",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span style={{ fontSize: 22 }}>{cfg.emoji}</span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: active ? "#10b981" : t.textSec,
                            }}
                          >
                            {cfg.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Send button */}
                <button
                  onClick={sendChallenge}
                  disabled={sending || !opponentEmail}
                  data-testid="send-challenge-btn"
                  style={{
                    width: "100%",
                    height: 54,
                    borderRadius: 16,
                    border: "none",
                    background:
                      sending || !opponentEmail
                        ? D
                          ? "#334155"
                          : "#e2e8f0"
                        : "linear-gradient(135deg,#10b981,#059669)",
                    color:
                      sending || !opponentEmail
                        ? t.textSec
                        : "#fff",
                    fontWeight: 800,
                    fontSize: 16,
                    cursor: sending || !opponentEmail ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    boxShadow:
                      sending || !opponentEmail
                        ? "none"
                        : "0 6px 20px rgba(16,185,129,0.35)",
                    transition: "all 0.2s",
                  }}
                >
                  {sending ? (
                    <>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          border: "2.5px solid rgba(255,255,255,0.4)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "spin 0.7s linear infinite",
                        }}
                      />
                      Sending…
                    </>
                  ) : (
                    <>⚔️ Send Challenge</>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════ PENDING TAB ═════════════════════════════ */}
          {activeTab === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.28 }}
              style={{ display: "flex", flexDirection: "column", gap: 28 }}
            >
              {/* ─── Received ─── */}
              <section>
                <div
                  style={{
                    background: "linear-gradient(135deg,#be123c,#9f1239)",
                    borderRadius: "16px 16px 0 0",
                    padding: "14px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 18 }}>📥</span>
                  <span
                    style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: "#fff",
                    }}
                  >
                    Received ({challenges.received?.length || 0})
                  </span>
                </div>

                <div
                  style={{
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                    borderTop: "none",
                    borderRadius: "0 0 16px 16px",
                    overflow: "hidden",
                  }}
                >
                  {!challenges.received?.length ? (
                    <div
                      style={{
                        padding: "28px 20px",
                        textAlign: "center",
                        color: t.textSec,
                        fontSize: 14,
                      }}
                    >
                      No pending challenges to respond to
                    </div>
                  ) : (
                    <div>
                      {challenges.received.map((challenge, idx) => {
                        const dc = diffConfig[challenge.difficulty] || {};
                        return (
                          <div
                            key={challenge.id}
                            style={{
                              padding: "16px 20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom:
                                idx < challenges.received.length - 1
                                  ? `1px solid ${t.border}`
                                  : "none",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              {/* Avatar */}
                              <div
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 14,
                                  background:
                                    "linear-gradient(135deg,#f43f5e,#e11d48)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontWeight: 800,
                                  fontSize: 20,
                                  flexShrink: 0,
                                }}
                              >
                                {initials(challenge.challenger_name)}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <p
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 15,
                                    color: t.textPri,
                                    margin: "0 0 4px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {challenge.challenger_name}
                                </p>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      background: dc.accentBg || "#e2e8f0",
                                      color: dc.accentText || "#475569",
                                      borderRadius: 9999,
                                      padding: "2px 10px",
                                      fontSize: 12,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {dc.emoji} {challenge.difficulty}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: t.textSec,
                                    }}
                                  >
                                    {fmt(challenge.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Buttons */}
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexShrink: 0,
                              }}
                            >
                              <button
                                onClick={() =>
                                  respondToChallenge(challenge.id, false)
                                }
                                data-testid={`decline-${challenge.id}`}
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 10,
                                  border: "1px solid #fecdd3",
                                  background: D ? "#2d0a13" : "#fff1f2",
                                  color: "#e11d48",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 18,
                                  fontWeight: 800,
                                }}
                                title="Decline"
                              >
                                ✕
                              </button>
                              <button
                                onClick={() =>
                                  respondToChallenge(challenge.id, true)
                                }
                                data-testid={`accept-${challenge.id}`}
                                style={{
                                  padding: "0 16px",
                                  height: 38,
                                  borderRadius: 10,
                                  border: "none",
                                  background:
                                    "linear-gradient(135deg,#10b981,#059669)",
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: 13,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                ✓ Accept
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>

              {/* ─── Sent ─── */}
              <section>
                <div
                  style={{
                    background: "linear-gradient(135deg,#b45309,#92400e)",
                    borderRadius: "16px 16px 0 0",
                    padding: "14px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 18 }}>📤</span>
                  <span
                    style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: "#fff",
                    }}
                  >
                    Sent ({challenges.sent?.length || 0})
                  </span>
                </div>

                <div
                  style={{
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                    borderTop: "none",
                    borderRadius: "0 0 16px 16px",
                    overflow: "hidden",
                  }}
                >
                  {!challenges.sent?.length ? (
                    <div
                      style={{
                        padding: "28px 20px",
                        textAlign: "center",
                        color: t.textSec,
                        fontSize: 14,
                      }}
                    >
                      No pending sent challenges
                    </div>
                  ) : (
                    <div>
                      {challenges.sent.map((challenge, idx) => {
                        const dc = diffConfig[challenge.difficulty] || {};
                        return (
                          <div
                            key={challenge.id}
                            style={{
                              padding: "16px 20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom:
                                idx < challenges.sent.length - 1
                                  ? `1px solid ${t.border}`
                                  : "none",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                              }}
                            >
                              {/* Avatar */}
                              <div
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 14,
                                  background:
                                    "linear-gradient(135deg,#f59e0b,#d97706)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontWeight: 800,
                                  fontSize: 20,
                                  flexShrink: 0,
                                }}
                              >
                                {initials(challenge.opponent_name)}
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 15,
                                    color: t.textPri,
                                    margin: "0 0 4px",
                                  }}
                                >
                                  {challenge.opponent_name}
                                </p>
                                {/* Awaiting spinner dot */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    color: t.textSec,
                                    fontSize: 12,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: "50%",
                                      background: "#f59e0b",
                                      animation:
                                        "spinnerDot 1.4s ease-in-out infinite",
                                      display: "inline-block",
                                    }}
                                  />
                                  Awaiting response...
                                </div>
                              </div>
                            </div>
                            <span
                              style={{
                                background: dc.accentBg || "#e2e8f0",
                                color: dc.accentText || "#475569",
                                borderRadius: 9999,
                                padding: "4px 12px",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {dc.emoji} {challenge.difficulty}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            </motion.div>
          )}

          {/* ═══════════════════════ ACTIVE TAB ══════════════════════════════ */}
          {activeTab === "active" && (
            <motion.div
              key="active"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.28 }}
            >
              <h3
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: t.textSec,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  margin: "0 0 16px 2px",
                }}
              >
                Active Challenges
              </h3>

              {!challenges.active?.length ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "56px 24px",
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: 20,
                    color: t.textSec,
                  }}
                >
                  <div style={{ fontSize: 52, marginBottom: 14 }}>⚔️</div>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: t.textPri,
                      margin: "0 0 6px",
                    }}
                  >
                    No active challenges
                  </p>
                  <p style={{ fontSize: 14 }}>
                    Accept a challenge or send one to get started!
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {challenges.active.map((challenge) => {
                    const dc = diffConfig[challenge.difficulty] || {};
                    // Show the correct opponent: if I'm the challenger, opponent is the
                    // challenged person; if I'm the challenged, opponent is the challenger.
                    const iAmChallenger = challenge.challenger_id === user?.id;
                    const oppName = iAmChallenger
                      ? (challenge.challenged_name || challenge.opponent_name || "Opponent")
                      : (challenge.challenger_name || "Opponent");
                    return (
                      <div
                        key={challenge.id}
                        style={{
                          background: t.surface,
                          border: `1px solid ${t.border}`,
                          borderRadius: 20,
                          overflow: "hidden",
                        }}
                      >
                        {/* VS battle header */}
                        <div
                          style={{
                            background: D
                              ? "linear-gradient(135deg,#0f2027,#1e3a4a)"
                              : "linear-gradient(135deg,#ecfdf5,#d1fae5)",
                            padding: "20px 24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 14,
                              flex: 1,
                            }}
                          >
                            {/* Challenger avatar placeholder */}
                            <div
                              style={{
                                width: 46,
                                height: 46,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg,#10b981,#059669)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontWeight: 800,
                                fontSize: 18,
                              }}
                            >
                              You
                            </div>
                            <span
                              style={{
                                fontSize: 22,
                                fontWeight: 900,
                                color: t.textSec,
                              }}
                            >
                              ⚔️
                            </span>
                            <div
                              style={{
                                width: 46,
                                height: 46,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg,#f43f5e,#e11d48)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontWeight: 800,
                                fontSize: 18,
                              }}
                            >
                              {initials(oppName)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p
                                style={{
                                  fontWeight: 700,
                                  fontSize: 15,
                                  color: t.textPri,
                                  margin: "0 0 4px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                vs {oppName}
                              </p>
                              <span
                                style={{
                                  background: dc.accentBg || "#e2e8f0",
                                  color: dc.accentText || "#475569",
                                  borderRadius: 9999,
                                  padding: "2px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                {dc.emoji} {challenge.difficulty}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Start Battle button */}
                        <div style={{ padding: "16px 24px" }}>
                          <button
                            onClick={() => startChallengeQuiz(challenge.id)}
                            data-testid={`start-challenge-${challenge.id}`}
                            style={{
                              width: "100%",
                              height: 52,
                              borderRadius: 14,
                              border: "none",
                              background:
                                "linear-gradient(135deg,#10b981,#059669)",
                              color: "#fff",
                              fontWeight: 800,
                              fontSize: 16,
                              cursor: "pointer",
                              animation: "glow 2s ease-in-out infinite",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                            }}
                          >
                            ⚔️ Start Battle!
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* ═══════════════════════ LEADERBOARD TAB ════════════════════════ */}
          {activeTab === "leaderboard" && (
            <motion.div
              key="leaderboard"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.28 }}
            >
              {/* Header */}
              <div
                style={{
                  background: "linear-gradient(135deg,#d97706,#b45309)",
                  borderRadius: "20px 20px 0 0",
                  padding: "24px 24px 20px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 6 }}>🏆</div>
                <h2
                  style={{
                    fontFamily: "Nunito, sans-serif",
                    fontWeight: 800,
                    fontSize: 22,
                    color: "#fff",
                    margin: 0,
                  }}
                >
                  Challengers Leaderboard
                </h2>
              </div>

              <div
                style={{
                  background: t.surface,
                  border: `1px solid ${t.border}`,
                  borderTop: "none",
                  borderRadius: "0 0 20px 20px",
                  padding: 20,
                }}
              >
                {/* Toggle */}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginBottom: 20,
                  }}
                >
                  {[
                    { id: "monthly", label: "Monthly" },
                    { id: "allTime", label: "All Time" },
                  ].map((p) => {
                    const active = leaderboardPeriod === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setLeaderboardPeriod(p.id)}
                        data-testid={
                          p.id === "monthly"
                            ? "leaderboard-monthly"
                            : "leaderboard-alltime"
                        }
                        style={{
                          flex: 1,
                          height: 40,
                          borderRadius: 10,
                          border: active ? "none" : `1px solid ${t.border}`,
                          background: active
                            ? "linear-gradient(135deg,#f59e0b,#d97706)"
                            : t.surface2,
                          color: active ? "#fff" : t.textSec,
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>

                {/* Table */}
                {!leaderboard[leaderboardPeriod]?.length ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: t.textSec,
                    }}
                  >
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🏆</div>
                    <p style={{ fontWeight: 700, color: t.textPri, margin: "0 0 4px" }}>
                      No challenge winners yet
                    </p>
                    <p style={{ fontSize: 13 }}>Be the first to win!</p>
                  </div>
                ) : (
                  <div>
                    {/* Table header */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "36px 1fr 56px 72px 80px",
                        gap: 8,
                        padding: "0 12px 8px",
                        borderBottom: `1px solid ${t.border}`,
                        fontSize: 11,
                        fontWeight: 700,
                        color: t.textSec,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      <span>#</span>
                      <span>Name</span>
                      <span style={{ textAlign: "center" }}>Wins</span>
                      <span style={{ textAlign: "center" }}>Win Rate</span>
                      <span style={{ textAlign: "center" }}>Challenges</span>
                    </div>

                    {leaderboard[leaderboardPeriod].map((entry, idx) => {
                      const rowBg = podiumRowStyle(idx, D, t);
                      const rankEmoji =
                        idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : null;
                      return (
                        <div
                          key={entry.user_id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "36px 1fr 56px 72px 80px",
                            gap: 8,
                            padding: "11px 12px",
                            borderRadius: 10,
                            background: rowBg,
                            marginTop: 4,
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 800,
                              fontSize: 15,
                              color: t.textSec,
                              textAlign: "center",
                            }}
                          >
                            {rankEmoji || `${entry.rank}`}
                          </span>
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: t.textPri,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {entry.name}
                          </span>
                          <span
                            style={{
                              textAlign: "center",
                              fontWeight: 700,
                              fontSize: 14,
                              color: "#10b981",
                            }}
                          >
                            {entry.wins}
                          </span>
                          <span
                            style={{
                              textAlign: "center",
                              fontWeight: 600,
                              fontSize: 13,
                              color: t.textSec,
                            }}
                          >
                            {entry.win_rate}%
                          </span>
                          <span
                            style={{
                              textAlign: "center",
                              fontWeight: 600,
                              fontSize: 13,
                              color: t.textSec,
                            }}
                          >
                            {entry.total_challenges}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════ HISTORY TAB ════════════════════════════ */}
          {activeTab === "history" && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.28 }}
            >
              <h3
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: t.textSec,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  margin: "0 0 16px 2px",
                }}
              >
                Your Challenge History
              </h3>

              {!history.length ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "56px 24px",
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: 20,
                    color: t.textSec,
                  }}
                >
                  <div style={{ fontSize: 48, marginBottom: 14 }}>📋</div>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: t.textPri,
                      margin: "0 0 6px",
                    }}
                  >
                    No completed challenges yet
                  </p>
                  <p style={{ fontSize: 14 }}>
                    Complete a challenge to see your history here
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  {history.map((h) => {
                    const won = h.you_won;
                    const tie = h.is_tie;

                    const outcomeEmoji = won ? "🏆" : tie ? "🤝" : "😔";
                    const outcomeLabel = won ? "Won" : tie ? "Tie" : "Lost";
                    const outcomeColor = won
                      ? "#f59e0b"
                      : tie
                      ? "#64748b"
                      : "#e11d48";
                    const cardBorder = won
                      ? D
                        ? "#2d2000"
                        : "#fef3c7"
                      : tie
                      ? t.border
                      : D
                      ? "#2d0a13"
                      : "#ffe4e6";
                    const leftAccent = won
                      ? "linear-gradient(180deg,#f59e0b,#d97706)"
                      : tie
                      ? "linear-gradient(180deg,#94a3b8,#64748b)"
                      : "linear-gradient(180deg,#f43f5e,#be123c)";

                    return (
                      <div
                        key={h.challenge_id}
                        style={{
                          background: t.surface,
                          border: `1px solid ${cardBorder}`,
                          borderRadius: 16,
                          overflow: "hidden",
                          display: "flex",
                        }}
                      >
                        {/* Left accent stripe */}
                        <div
                          style={{
                            width: 6,
                            background: leftAccent,
                            flexShrink: 0,
                          }}
                        />

                        {/* Content */}
                        <div
                          style={{
                            flex: 1,
                            padding: "14px 18px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 14,
                            }}
                          >
                            {/* Outcome icon */}
                            <div
                              style={{
                                width: 46,
                                height: 46,
                                borderRadius: 12,
                                background: won
                                  ? D
                                    ? "#2d2000"
                                    : "#fffbeb"
                                  : tie
                                  ? D
                                    ? "#1c2533"
                                    : "#f8fafc"
                                  : D
                                  ? "#2d0a13"
                                  : "#fff1f2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 22,
                                flexShrink: 0,
                              }}
                            >
                              {outcomeEmoji}
                            </div>
                            <div>
                              <p
                                style={{
                                  fontWeight: 700,
                                  fontSize: 15,
                                  color: t.textPri,
                                  margin: "0 0 4px",
                                }}
                              >
                                vs {h.opponent_name}
                              </p>
                              <p
                                style={{ fontSize: 13, color: t.textSec, margin: 0 }}
                              >
                                {h.your_score}/15 vs {h.opponent_score}/15
                              </p>
                            </div>
                          </div>

                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <p
                              style={{
                                fontWeight: 800,
                                fontSize: 15,
                                color: outcomeColor,
                                margin: "0 0 3px",
                              }}
                            >
                              {outcomeLabel}
                            </p>
                            {won && h.gp_earned !== undefined && (
                              <p
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#10b981",
                                  margin: "0 0 3px",
                                }}
                              >
                                +{h.gp_earned} GP
                              </p>
                            )}
                            <p
                              style={{
                                fontSize: 11,
                                color: t.textSec,
                                margin: 0,
                              }}
                            >
                              {fmt(h.completed_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ChallengePage;
